import { TrackedMap } from 'tracked-built-ins';
import { adapterHelper } from 'client-portal/utils/clinicianSearch/send-all-catalog';
import { reportClinicianSearchError } from 'client-portal/utils/clinicianSearch/report-error';
import { tracked } from '@glimmer/tracking';
import Service, { service } from '@ember/service';

const STATUS_PENDING = 'pending';
const STATUS_READY = 'ready';
// Must not exceed the endpoint's MAX_CLINICIAN_IDS, which it enforces with a 422. A batch's
// cost is mostly fixed rather than per-clinician (one bulk schedule prefetch either way), so
// 25 made a 169-clinician practice pay that prefetch seven times over seven sequential round
// trips. Measured against production data, four times the clinicians costs 1.6x the time.
const BATCH_SIZE = 100;
// Batches are independent, so running them one at a time made the wall clock the sum of every
// batch. The cap keeps a many-clinician practice from opening an unbounded number of expensive
// requests at once.
const MAX_CONCURRENT_BATCHES = 3;
const SYNTHETIC_BLOCK_SOURCE = 'nextSlot';

export default class ClinicianSearchNextSlotsService extends Service {
  @service store;

  @tracked enrichmentVersion = 0;
  @tracked isEnriching = false;

  statuses = new TrackedMap();

  #cache = new Map();
  #errorKeys = new Set();
  #currentGeneration = 0;
  #onBatchComplete = null;
  #inFlight = 0;
  #slotWaiters = [];

  setOnBatchComplete(callback) {
    this.#onBatchComplete = callback;
  }

  resetState() {
    this.#currentGeneration += 1;
    this.#cache.clear();
    this.#errorKeys.clear();
    this.statuses.clear();
    this.enrichmentVersion = 0;
    this.isEnriching = false;
  }

  statusFor(clinicianId) {
    return this.statuses.get(String(clinicianId));
  }

  enrichmentStatusFor(clinicianId, filters = {}) {
    let id = String(clinicianId);
    let normalized = this.#normalizeFilterArgs(filters);
    let chipKey = this.#cacheKeyFor(id, normalized);

    if (this.#errorKeys.has(chipKey)) {
      return 'error';
    }
    if (this.#cache.has(chipKey)) {
      if (this.#cache.get(chipKey)?.at) {
        return 'hasSlot';
      }
      // No date in the ~30-day window, but catalog blocks mean they can
      // still book further out — rank/select differently from true empty.
      let record = this.store.peekRecord('clinician', id);
      if (record?.availabilityBlocks?.length) {
        return 'laterSlot';
      }
      return 'noSlot';
    }
    return 'unknown';
  }

  nextAvailableAt(clinicianId, filters = {}) {
    let id = String(clinicianId);
    let normalized = this.#normalizeFilterArgs(filters);
    let chipKey = this.#cacheKeyFor(id, normalized);

    if (!this.#cache.has(chipKey)) {
      return null;
    }
    return this.#cache.get(chipKey)?.at ?? null;
  }

  async enrichCohort(cohort, filters = {}) {
    if (!Array.isArray(cohort) || cohort.length === 0) {
      return;
    }

    let gen = ++this.#currentGeneration;
    try {
      await this.#runEnrichLoop(cohort, filters, gen);
    } catch (err) {
      reportClinicianSearchError(err);
    }
  }

  async #runEnrichLoop(cohort, filters, gen) {
    this.isEnriching = true;
    try {
      let normalized = this.#normalizeFilterArgs(filters);
      let queue = [];
      for (let cursor = 0; cursor < cohort.length; cursor += BATCH_SIZE) {
        queue.push(cohort.slice(cursor, cursor + BATCH_SIZE));
      }

      let workers = Math.min(MAX_CONCURRENT_BATCHES, queue.length);
      // Workers past the cap park in #acquireSlot rather than opening a request, so overlapping
      // generations share the cap instead of each getting their own.
      await Promise.all(
        Array.from({ length: workers }, () => this.#drainQueue(queue, normalized, gen))
      );
    } finally {
      if (gen === this.#currentGeneration && !this.#isDestroyedOrDestroying()) {
        this.isEnriching = false;
      }
    }
  }

  async #drainQueue(queue, normalized, gen) {
    while (queue.length) {
      if (gen !== this.#currentGeneration || this.#isDestroyedOrDestroying()) {
        return;
      }

      let batchIds = queue
        .shift()
        .map(record => String(record?.id ?? ''))
        .filter(id => id && !this.#hasCached(id, normalized));

      if (batchIds.length) {
        let waitForSlot = this.#acquireSlot();
        if (waitForSlot) {
          await waitForSlot;
          if (gen !== this.#currentGeneration || this.#isDestroyedOrDestroying()) {
            this.#releaseSlot();
            return;
          }
        }

        try {
          await this.#flush(
            {
              ids: batchIds,
              officeIds: normalized.officeIds,
              cptCodeRateId: normalized.cptCodeRateId,
              weekdays: normalized.weekdays,
              timeBuckets: normalized.timeBuckets,
            },
            gen
          );
        } catch (err) {
          reportClinicianSearchError(err);
          if (gen === this.#currentGeneration) {
            this.#markBatchErrored(batchIds, normalized);
          }
        } finally {
          this.#releaseSlot();
        }
      }

      if (gen !== this.#currentGeneration || this.#isDestroyedOrDestroying()) {
        return;
      }

      this.enrichmentVersion += 1;
    }
  }

  // Returns null when a slot was free, so an uncontended batch still reaches the wire in the
  // same tick — awaiting here would let a cohort queued later in the same tick cancel it.
  #acquireSlot() {
    if (this.#inFlight < MAX_CONCURRENT_BATCHES) {
      this.#inFlight += 1;
      return null;
    }
    return new Promise(resolve => this.#slotWaiters.push(resolve));
  }

  #releaseSlot() {
    // Hand the slot straight to the next waiter so the count never dips and lets a fourth in.
    let next = this.#slotWaiters.shift();
    if (next) {
      next();
    } else {
      this.#inFlight -= 1;
    }
  }

  #isDestroyedOrDestroying() {
    return this.isDestroyed || this.isDestroying;
  }

  #markBatchErrored(ids, normalized) {
    if (this.#isDestroyedOrDestroying()) {
      return;
    }
    for (let id of ids) {
      let cacheKey = this.#cacheKeyFor(id, normalized);
      this.#errorKeys.add(cacheKey);
      this.#cache.set(cacheKey, { at: null, block: null });
      this.statuses.set(id, STATUS_READY);
    }
  }

  #hasCached(id, normalized) {
    return this.#cache.has(this.#cacheKeyFor(id, normalized));
  }

  #cacheKeyFor(id, { officeIds, cptCodeRateId, weekdays, timeBuckets }) {
    let officeKey = officeIds.join(',');
    let cptKey = cptCodeRateId ?? '';
    let weekdaysKey = weekdays.join(',');
    let bucketsKey = timeBuckets.join(',');
    return `${id}:${officeKey}:${cptKey}:${weekdaysKey}:${bucketsKey}`;
  }

  #normalizeFilterArgs(filters = {}) {
    return {
      officeIds: this.#normalizeIntList(filters.officeIds),
      cptCodeRateId: this.#normalizeId(filters.cptCodeRateId),
      weekdays: this.#normalizeIntList(filters.weekdays),
      timeBuckets: this.#normalizeIntList(filters.timeBuckets),
    };
  }

  #notifyBatchComplete(ids) {
    try {
      this.#onBatchComplete?.(ids);
    } catch (err) {
      reportClinicianSearchError(err);
    }
  }

  async #flush({ ids, officeIds, cptCodeRateId, weekdays, timeBuckets }, gen) {
    let normalized = { officeIds, cptCodeRateId, weekdays, timeBuckets };
    let toFetch = [];

    for (let id of ids) {
      let cacheKey = this.#cacheKeyFor(id, normalized);
      if (this.#cache.has(cacheKey)) {
        let entry = this.#cache.get(cacheKey);
        this.#applyToStore(id, entry?.at ?? null, entry?.block ?? null);
      } else {
        toFetch.push(id);
        this.statuses.set(id, STATUS_PENDING);
      }
    }

    if (toFetch.length === 0) {
      this.#notifyBatchComplete(ids);
      return;
    }

    /* eslint-disable camelcase -- next-slots wire format matches the Rails API */
    let body = {
      clinician_ids: toFetch.map(id => parseInt(id, 10)).filter(Number.isFinite),
    };
    if (officeIds.length) {
      body.office_ids = officeIds;
    }
    if (cptCodeRateId) {
      body.cpt_code_rate_id = parseInt(cptCodeRateId, 10);
    }
    if (weekdays.length) {
      body.weekdays = weekdays;
    }
    if (timeBuckets.length) {
      body.time_buckets = timeBuckets;
    }
    /* eslint-enable camelcase */

    let adapter = this.store.adapterFor('clinician');
    let url = adapterHelper(adapter, { nextSlots: true });
    let response = await adapter.ajax(url, 'POST', { data: body });

    // A newer cohort may have superseded us while this request was on the wire. Drop the
    // response before it touches cache, store, or statuses.
    if (gen !== this.#currentGeneration || this.#isDestroyedOrDestroying()) {
      return;
    }

    let rows = response?.data ?? [];

    for (let row of rows) {
      let id = String(row.clinician_id);
      let nextAvailableAt = row.next_available_at ?? null;
      let availabilityBlock = row.availability_block ?? null;
      this.#cache.set(this.#cacheKeyFor(id, normalized), {
        at: nextAvailableAt,
        block: availabilityBlock,
      });
      this.#applyToStore(id, nextAvailableAt, availabilityBlock);
    }

    let returnedIds = new Set(rows.map(row => String(row.clinician_id)));
    for (let id of toFetch) {
      if (!returnedIds.has(id)) {
        this.#cache.set(this.#cacheKeyFor(id, normalized), { at: null, block: null });
        this.#applyToStore(id, null, null);
      }
    }

    this.#notifyBatchComplete(ids);
  }

  #applyToStore(id, nextAvailableAt, availabilityBlock = null) {
    let stringId = String(id);
    // Always push — may land before catalog seed.
    let existing = this.store.peekRecord('clinician', stringId);
    let attributes = { nextAvailability: nextAvailableAt };

    if (availabilityBlock) {
      attributes.availabilityBlocks = this.#mergeAvailabilityBlocks(
        existing?.availabilityBlocks,
        availabilityBlock
      );
    }

    this.store.push({
      data: {
        id: stringId,
        type: 'clinician',
        attributes,
      },
    });
    this.statuses.set(stringId, STATUS_READY);
  }

  #mergeAvailabilityBlocks(existing, incoming) {
    let catalog = (Array.isArray(existing) ? existing : []).filter(
      block => block?.source !== SYNTHETIC_BLOCK_SOURCE
    );
    let tagged = { ...incoming, source: SYNTHETIC_BLOCK_SOURCE };
    let signature = this.#blockSignature(tagged);

    if (catalog.some(block => this.#blockSignature(block) === signature)) {
      return catalog;
    }

    return [...catalog, tagged];
  }

  #blockSignature(block) {
    let weekdays = [...(block?.weekdays ?? [])].map(Number).sort((a, b) => a - b);
    let timeBuckets = [...(block?.timeBuckets ?? [])].map(Number).sort((a, b) => a - b);
    let officeIds = [...(block?.officeIds ?? [])].map(String).sort();
    let cptCodeRateIds = [...(block?.cptCodeRateIds ?? [])].map(String).sort();
    return [weekdays, timeBuckets, officeIds, cptCodeRateIds].map(part => part.join(',')).join('|');
  }

  #normalizeId(value) {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    let str = String(value).trim();
    return str.length ? str : null;
  }

  #normalizeIntList(values) {
    if (!Array.isArray(values)) {
      return [];
    }
    return [
      ...new Set(
        values
          .map(value => (typeof value === 'number' ? value : parseInt(String(value).trim(), 10)))
          .filter(Number.isFinite)
      ),
    ].sort((a, b) => a - b);
  }
}
